import { Icon, Tooltip, UseFormReturn } from '@gcl/react-core';
import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { ETransmittalType } from '@transmittal/transmittal.constants';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import { ITransmittalItemRowEditingRef, ITransmittalItemTableEditingRef } from './transmittalItemTableForm.models';
import { useFormPrefix, useSubmitRow } from './transmittalItemTableForm.hooks';
import { ETransmittalConfirmationActionType } from '../transmittalDetailPage.constants';
import { useTransmittalDetailPageConfirmationModal } from '../transmittalDetailPage.hooks';

interface ITransmittalItemTableFormHeaderStateButtons {
  masterScheduleId: number;
  transmittalId: number;
  transmittalType: ETransmittalType;
  refRowEditingEvents: MutableRefObject<ITransmittalItemRowEditingRef>;
  setEditingRowCount: Dispatch<SetStateAction<number>>;
  editingRowCount: number;
  useFormMethods: UseFormReturn;
  queryKey: QueryKey;
  isAnyActionLoading: boolean;
  tableIndex: number;
  transmittalItemTableEditingRef: MutableRefObject<ITransmittalItemTableEditingRef>;
  setRowUploading: (index: number, isUploading: boolean) => void;
  isSubmitEnabled: boolean;
}
export const TransmittalItemTableFormHeaderStateButtons = ({
  masterScheduleId,
  transmittalId,
  transmittalType,
  refRowEditingEvents,
  setEditingRowCount,
  editingRowCount,
  useFormMethods,
  queryKey,
  isAnyActionLoading,
  tableIndex,
  transmittalItemTableEditingRef,
  setRowUploading,
  isSubmitEnabled,
}: ITransmittalItemTableFormHeaderStateButtons) => {
  const queryClient = useQueryClient();

  const {
    renderConfirmationPortal: renderUploadRevisionWithSameFileNameConfirmationModal,
    openConfirmationPortal: openUploadRevisionWithSameFileNameConfirmationModal,
  } = useTransmittalDetailPageConfirmationModal(() => {
    Object.entries(refRowEditingEvents.current).map(([key, events]) => {
      if (events.setRowState) events.setRowState('Loading');
    });
    onClickSubmitTable(tableIndex, refRowEditingEvents);
  });

  const { tablePrefix } = useFormPrefix(tableIndex);

  const {
    onClickSubmitTable,
    updateCacheFromUpdateTransmittalItemResponse,
    mutationUpsertTransmittalItemsAndRevisionsForTables,
    errorKeySeperator,
  } = useSubmitRow(masterScheduleId, transmittalId, transmittalType, useFormMethods);

  const {
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
    isError: isErrorUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisionsForTables;

  const apiDisplay = (
    <>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
            reset: resetUpsertTransmittalItemsAndRevisions,
            message: 'Update transmittal items success',
          },
        ]}
        callbackAfterToasted={() => {
          if (isSuccessUpsertTransmittalItemsAndRevisions) {
            const table = useFormMethods.getValues(tablePrefix) as IFormFileDrawingItem[];
            const newTable = table.map((row) => {
              return {
                ...row,
                file: undefined,
                form: {
                  ...row.form,
                  projectFile: undefined,
                  lastModifiedDateTime: undefined,
                },
              } as IFormFileDrawingItem;
            });
            useFormMethods.setValue(tablePrefix, newTable);
            updateCacheFromUpdateTransmittalItemResponse(dataUpsertTransmittalItemsAndRevisions?.items);
          }

          queryClient.invalidateQueries(queryKey);

          Object.entries(refRowEditingEvents.current).map(([key, events]) => {
            if (events.setRowState) events.setRowState('Idle');
          });
        }}
      />
      <ApiErrorDisplay
        errors={[
          {
            error: errorUpsertTransmittalItemsAndRevisions,
            genericError: 'Update transmittal items fail',
            reset: resetUpsertTransmittalItemsAndRevisions,
            keyExtractor: errorKeySeperator,
          },
        ]}
        setFormError={useFormMethods.setError}
        callbackAfterToast={() => {
          Object.entries(refRowEditingEvents.current).map(([key, events]) => {
            if (events.setRowState) events.setRowState('Editing');
          });
        }}
      />
    </>
  );

  if (editingRowCount <= 0) return apiDisplay;

  return (
    <>
      {apiDisplay}
      <div className="gcl-gems-transmittal-item-table-form-batch-action-container">
        <Tooltip overlay="Save all" id="saveAll" key="saveAll">
          <Icon
            disabled={isAnyActionLoading || !isSubmitEnabled || isLoadingUpsertTransmittalItemsAndRevisions}
            iconName="Check2All"
            className="gcl-ic-btn-xxl gcl-mr-xs gcl-gems-transmittal-item-file-display-check-btn"
            onClick={(e) => {
              openUploadRevisionWithSameFileNameConfirmationModal(
                e,
                ETransmittalConfirmationActionType.save,
                useFormMethods,
                tableIndex,
              );
            }}
          />
        </Tooltip>

        <Tooltip overlay="Cancel all" id="cancelAll" key="cancelAll">
          <Icon
            disabled={isAnyActionLoading || isLoadingUpsertTransmittalItemsAndRevisions}
            iconName="ReplyAll"
            className="gcl-ic-btn-xxl gcl-gems-transmittal-item-file-display-x-btn"
            onClick={() => {
              Object.values(refRowEditingEvents.current).map((row) => {
                if (row.setRowState) row.setRowState('Idle');
              });
              useFormMethods.resetField(`${tablePrefix}`);
              refRowEditingEvents.current = {};
              setEditingRowCount(0);
            }}
          />
        </Tooltip>
      </div>
      {renderUploadRevisionWithSameFileNameConfirmationModal(
        isLoadingUpsertTransmittalItemsAndRevisions,
        isErrorUpsertTransmittalItemsAndRevisions,
        isSuccessUpsertTransmittalItemsAndRevisions,
      )}
    </>
  );
};
